import { useState, useEffect, createContext, useContext, ReactNode } from "react";
import axios from "axios";
import { Lock, Key, AlertCircle, Loader2, ArrowLeft } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";

const BACKEND_URL = process.env.NODE_ENV === 'development' ? 'http://localhost:8000' : '';
const API = `${BACKEND_URL}/api`;

// Contexto de autenticación
interface AuthContextType {
  isAuthenticated: boolean;
  login: (pin: string) => Promise<boolean>;
  logout: () => void;
  token: string | null;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

// Provider de autenticación
export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(() => {
    return localStorage.getItem("admin_token");
  });
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    // Verificar token al cargar
    const verifyToken = async () => {
      if (!token) {
        setIsAuthenticated(false);
        setChecking(false);
        return;
      }

      try {
        await axios.get(`${API}/auth/verify`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setIsAuthenticated(true);
      } catch (err) {
        // Token inválido o expirado
        localStorage.removeItem("admin_token");
        setToken(null);
        setIsAuthenticated(false);
      } finally {
        setChecking(false);
      }
    };

    verifyToken();
  }, [token]);

  const login = async (pin: string): Promise<boolean> => {
    try {
      const res = await axios.post(`${API}/auth/login`, { pin });
      const newToken = res.data.access_token;
      localStorage.setItem("admin_token", newToken);
      setToken(newToken);
      setIsAuthenticated(true);
      return true;
    } catch (err) {
      return false;
    }
  };

  const logout = () => {
    localStorage.removeItem("admin_token");
    setToken(null);
    setIsAuthenticated(false);
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-[#030305] flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-cyan-400 animate-spin" />
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ isAuthenticated, login, logout, token }}>
      {children}
    </AuthContext.Provider>
  );
}

// Componente de login con PIN (6 dígitos)
export function AdminLogin() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [pinDigits, setPinDigits] = useState<string[]>(["", "", "", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handlePinDigit = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;

    const newDigits = [...pinDigits];
    newDigits[index] = value.slice(-1);
    setPinDigits(newDigits);

    // Auto-avanzar
    if (value && index < 5) {
      const nextInput = document.getElementById(`admin-pin-${index + 1}`);
      nextInput?.focus();
    }

    // Verificar si están todos (6 dígitos)
    if (newDigits.every(d => d !== "")) {
      verificarPin(newDigits.join(""));
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === "Backspace" && !pinDigits[index] && index > 0) {
      const prevInput = document.getElementById(`admin-pin-${index - 1}`);
      prevInput?.focus();
    }
  };

  const verificarPin = async (pin: string) => {
    setLoading(true);
    setError("");

    const success = await login(pin);

    if (!success) {
      setError("PIN incorrecto");
      setPinDigits(["", "", "", "", "", ""]);
      document.getElementById("admin-pin-0")?.focus();
    }

    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#030305] via-[#0a0a15] to-[#030305] text-white flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br from-amber-500/20 to-orange-600/20 border border-amber-500/30 mb-6">
            <Lock className="h-10 w-10 text-amber-400" />
          </div>
          <h1 className="text-3xl font-black mb-2">
            Acceso <span className="text-amber-400">Administrador</span>
          </h1>
          <p className="text-gray-400">Ingresa tu PIN de 6 dígitos</p>
        </div>

        <Card className="bg-[#0a0a0f]/80 backdrop-blur border-white/10">
          <CardContent className="p-6">
            <div className="flex items-center gap-2 mb-6">
              <Key className="h-5 w-5 text-amber-400" />
              <span className="text-gray-300 font-medium">PIN de Admin</span>
            </div>

            {/* PIN Input - 6 dígitos */}
            <div className="flex justify-center gap-2 mb-6">
              {pinDigits.map((digit, idx) => (
                <input
                  key={idx}
                  id={`admin-pin-${idx}`}
                  type="password"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handlePinDigit(idx, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(idx, e)}
                  className="w-11 h-14 text-center text-2xl font-bold bg-white/5 border-2 border-white/10 rounded-xl text-white focus:border-amber-500 focus:outline-none transition-colors"
                  autoFocus={idx === 0}
                />
              ))}
            </div>

            {error && (
              <div className="flex items-center gap-2 text-red-400 text-sm mb-4 justify-center">
                <AlertCircle className="h-4 w-4" />
                {error}
              </div>
            )}

            {loading && (
              <div className="flex justify-center mb-4">
                <Loader2 className="h-6 w-6 text-amber-400 animate-spin" />
              </div>
            )}

            <div className="text-center mt-6">
              <Button
                variant="ghost"
                onClick={() => navigate("/")}
                className="text-gray-500 hover:text-white"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Volver al Inicio
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// HOC para proteger rutas
export function ProtectedRoute({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth();

  if (!isAuthenticated) {
    return <AdminLogin />;
  }

  return <>{children}</>;
}

